import { Controller } from "@hotwired/stimulus"

// Multi-step form wizard controller
// Connects to data-controller="multistep-form"
export default class extends Controller {
    static targets = [
        "step", "indicator", "prevButton", "nextButton", "submitButton", "progressLine", "progressStep",
        // Funnel step review targets
        "reviewName", "reviewType", "reviewUrl", "reviewProducts", "reviewSummary",
        // Source input targets (for robust data retrieval)
        "sourceName", "sourceType", "sourceUrl"
    ]

    // ... (rest of class)

    updateFunnelStepReview() {
        // Fallback elements by ID (The "Nuclear Option")
        const nameInputById = document.getElementById("funnel_step_name_input")
        const urlInputById = document.getElementById("funnel_step_url_input")


        // Name Retrieval Strategy: Target -> ID -> Name Selector
        let name = "—"
        if (this.hasSourceNameTarget && this.sourceNameTarget.value !== "") {
            name = this.sourceNameTarget.value
        } else if (nameInputById && nameInputById.value !== "") {
            name = nameInputById.value
        } else {
            name = this.element.querySelector('input[name$="[name]"]')?.value || "—"
        }

        // Page Type Retrieval Strategy: Target -> Name Selector
        let pageType = "—"
        if (this.hasSourceTypeTarget) {
            const checked = this.sourceTypeTargets.find(radio => radio.checked)
            if (checked) pageType = checked.value
        } else {
            const checked = this.element.querySelector('input[name$="[page_type]"]:checked')
            if (checked) pageType = checked.value
        }

        // URL Retrieval Strategy: Target -> ID -> Name Selector
        let urlPath = "—"
        if (this.hasSourceUrlTarget && this.sourceUrlTarget.value !== "") {
            urlPath = this.sourceUrlTarget.value
        } else if (urlInputById && urlInputById.value !== "") {
            urlPath = urlInputById.value
        } else {
            urlPath = this.element.querySelector('input[name$="[url_path]"]')?.value || "—"
        }

        // robust selector for product_ids (handling funnel_step[product_ids][] or just product_ids[])
        const selectedProducts = this.element.querySelectorAll('input[name*="product_ids"]:checked').length

        if (this.hasReviewNameTarget) this.reviewNameTarget.textContent = name
        if (this.hasReviewTypeTarget) this.reviewTypeTarget.textContent = pageType.charAt(0).toUpperCase() + pageType.slice(1)
        if (this.hasReviewUrlTarget) this.reviewUrlTarget.textContent = `/${urlPath}`
        if (this.hasReviewProductsTarget) this.reviewProductsTarget.textContent = selectedProducts > 0 ? `${selectedProducts} selected` : "None"
    }
    static values = { current: { type: Number, default: 1 }, total: { type: Number, default: 4 } }

    connect() {
        this.detectContext()
        this.showStep(this.currentValue)
        this.updateButtons()
        this.updateIndicators()
        this.updateProgressLine()

        // Ensure input state is correct on load (for Edit views)
        // Use setTimeout to allow DOM to settle and avoid race conditions
        setTimeout(() => {
            if (!this.isFunnelStep) {
                this.toggleConfigSection()
            }
        }, 0)
    }

    validateSubmission(event) {
        if (!this.validateCurrentStep()) {
            event.preventDefault()
            event.stopPropagation()
            this.showError("Corrigez les erreurs avant d'enregistrer")
        }
        // If valid, let the form submit naturally. 
        // Since we added novalidate to the form, the browser won't block it based on hidden fields.
    }

    detectContext() {
        // Check if we're in funnel step form or product form
        this.isFunnelStep = this.element.querySelector('input[name="funnel_step[name]"]') !== null
    }

    nextStep() {
        this.next()
    }

    prevStep() {
        this.previous()
    }

    next() {
        if (this.validateCurrentStep()) {
            if (this.currentValue < this.totalValue) {
                this.currentValue++
                // Skip Products step (step 2) for landing/confirmation pages
                if (this.shouldSkipCurrentStep()) {
                    this.currentValue++
                }
                this.animateStepTransition()
            }
        }
    }

    previous() {
        if (this.currentValue > 1) {
            this.currentValue--
            // Skip Products step (step 2) for landing/confirmation pages
            if (this.shouldSkipCurrentStep()) {
                this.currentValue--
            }
            this.animateStepTransition()
        }
    }

    shouldSkipCurrentStep() {
        if (!this.isFunnelStep) return false

        const pageType = this.element.querySelector('input[name="funnel_step[page_type]"]:checked')?.value
        const skipProductsStep = (pageType === "landing" || pageType === "confirmation")

        // Step 2 is the Products step - skip it for landing/confirmation
        return skipProductsStep && this.currentValue === 2
    }

    goToStep(event) {
        const step = parseInt(event.currentTarget.dataset.step)
        if (step <= this.currentValue) {
            this.currentValue = step
            this.animateStepTransition()
        }
    }

    animateStepTransition() {
        // Hide all steps first
        this.stepTargets.forEach(step => {
            step.style.display = "none"
            step.classList.add("hidden")
        })

        // Show current step with animation
        const currentStep = this.stepTargets[this.currentValue - 1]
        if (currentStep) {
            currentStep.style.display = "block"
            currentStep.classList.remove("hidden")
            currentStep.classList.remove("animate-fadeIn")
            // Trigger reflow for animation restart
            void currentStep.offsetWidth
            currentStep.classList.add("animate-fadeIn")
        }

        this.updateButtons()
        this.updateIndicators()
        this.updateProgressLine()

        // Update review panel if we're on the last step (review step)
        if (this.currentValue === this.totalValue) {
            this.updateReviewPanel()
        }

        this.scrollToTop()
    }

    showStep(stepNumber) {
        this.stepTargets.forEach((step, index) => {
            if (index + 1 === stepNumber) {
                step.style.display = "block"
                step.classList.remove("hidden")
            } else {
                step.style.display = "none"
                step.classList.add("hidden")
            }
        })

        // Handle step 2: show digital OR reservation based on product_nature (for products)
        if (stepNumber === 2 && !this.isFunnelStep) {
            this.toggleConfigSection()
        }

        // Update review panel on last step
        if (stepNumber === this.totalValue) {
            this.updateReviewPanel()
        }
    }

    toggleConfigSection() {
        const digitalSection = document.getElementById("config-digital")
        const reservationSection = document.getElementById("config-reservation")
        const selectedNature = document.querySelector('input[name="product[product_nature]"]:checked') ||
            document.querySelector('input[name="super_admin_product[product_nature]"]:checked')

        console.log("[Debug] toggleConfigSection", {
            hasDigital: !!digitalSection,
            hasReservation: !!reservationSection,
            natureValue: selectedNature?.value
        })

        if (!digitalSection || !reservationSection) return

        const toggleInputs = (container, disabled) => {
            const inputs = container.querySelectorAll("input, select, textarea, button")
            console.log(`[Debug] Toggling ${container.id}: ${disabled ? 'DISABLE' : 'ENABLE'} (${inputs.length} inputs)`)
            inputs.forEach(input => {
                if (disabled) {
                    input.setAttribute("disabled", "disabled")
                } else {
                    input.removeAttribute("disabled")
                }
            })
        }

        if (selectedNature && selectedNature.value === "reservation") {
            digitalSection.classList.add("hidden")
            toggleInputs(digitalSection, true)

            reservationSection.classList.remove("hidden")
            toggleInputs(reservationSection, false)
        } else {
            console.log("[Debug] Defaulting to digital (or no selection)")
            digitalSection.classList.remove("hidden")
            toggleInputs(digitalSection, false)

            reservationSection.classList.add("hidden")
            toggleInputs(reservationSection, true)
        }
    }

    updateButtons() {
        // Previous button - hide on step 1
        if (this.hasPrevButtonTarget) {
            this.prevButtonTarget.style.display = this.currentValue === 1 ? "none" : "inline-flex"
        }

        // Next button - hide on last step
        if (this.hasNextButtonTarget) {
            this.nextButtonTarget.style.display = this.currentValue === this.totalValue ? "none" : "inline-flex"
        }

        // Submit button - show only on last step
        if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.style.display = this.currentValue === this.totalValue ? "inline-flex" : "none"
        }
    }

    updateIndicators() {
        // Support both indicator and progressStep targets for backwards compatibility
        const indicators = this.hasIndicatorTarget ? this.indicatorTargets :
            this.hasProgressStepTarget ? this.progressStepTargets : []

        indicators.forEach((indicator, index) => {
            const stepNum = index + 1

            // Reset classes
            // ⛔ "bg-beb-surface" manquait dans cette liste, alors que les deux
            // gabarits d'indicateur le portent en dur. La pastille de l'étape
            // COURANTE recevait bien bg-beb-brand, mais bg-beb-surface gagnait :
            // chiffre blanc (--brand-fg) sur fond blanc, contraste mesuré 1,03.
            indicator.classList.remove(
                "bg-beb-brand", "bg-beb-success", "bg-beb-line", "bg-beb-surface", "bg-beb-surface-2",
                "text-beb-brand-fg", "text-beb-success-fg", "text-beb-fg-2", "text-beb-fg-3",
                "shadow-lg"
            )

            const stepNumberEl = indicator.querySelector("[data-step-number]")

            if (stepNum < this.currentValue) {
                // Completed steps - green with checkmark
                indicator.classList.add("bg-beb-success", "text-beb-success-fg", "shadow-lg")
                if (stepNumberEl) stepNumberEl.innerHTML = "✓"
                else indicator.innerHTML = "✓"
            } else if (stepNum === this.currentValue) {
                // Current step - indigo gradient
                indicator.classList.add("bg-beb-brand", "text-beb-brand-fg", "shadow-lg")
                if (stepNumberEl) stepNumberEl.textContent = stepNum
                else indicator.textContent = stepNum
            } else {
                // Future steps - gray
                indicator.classList.add("bg-beb-line", "text-beb-fg-2")
                if (stepNumberEl) stepNumberEl.textContent = stepNum
                else indicator.textContent = stepNum
            }
        })
    }

    updateProgressLine() {
        if (!this.hasProgressLineTarget) return

        // Calculate progress percentage
        const progress = ((this.currentValue - 1) / (this.totalValue - 1)) * 100
        this.progressLineTarget.style.width = `${progress}%`
    }

    validateCurrentStep() {
        const currentStepEl = this.stepTargets[this.currentValue - 1]
        if (!currentStepEl) return true

        const requiredFields = currentStepEl.querySelectorAll("[required]:not([disabled])")
        let isValid = true
        let firstInvalid = null

        requiredFields.forEach(field => {
            // Skip hidden fields
            if (field.closest(".hidden") || field.closest("[style*='display: none']")) return

            if (!field.value.trim()) {
                isValid = false
                field.classList.add("border-beb-danger", "bg-beb-danger-soft")
                field.classList.remove("border-beb-line")
                if (!firstInvalid) firstInvalid = field
            } else {
                field.classList.remove("border-beb-danger", "bg-beb-danger-soft")
                field.classList.add("border-beb-line")
            }
        })

        // Check for product_nature on step 1 - ONLY for product forms, not funnel steps
        if (this.currentValue === 1 && !this.isFunnelStep) {
            const natureSelected = document.querySelector('input[name="product[product_nature]"]:checked')
            if (!natureSelected) {
                isValid = false
                this.showError("Choisissez un type de produit")
                return isValid
            }
        }

        // For funnel steps, check page_type is selected on step 1
        if (this.currentValue === 1 && this.isFunnelStep) {
            const pageTypeSelected = document.querySelector('input[name="funnel_step[page_type]"]:checked')
            if (!pageTypeSelected) {
                isValid = false
                this.showError("Choisissez un type de page")
                return isValid
            }
        }

        if (!isValid && firstInvalid) {
            firstInvalid.focus()
            this.showError("Remplissez tous les champs obligatoires")
        }

        return isValid
    }

    showError(message) {
        // Remove existing toast if any
        const existing = document.querySelector("[data-error-toast]")
        if (existing) existing.remove()

        // Create premium error toast
        const toast = document.createElement("div")
        toast.setAttribute("data-error-toast", "")
        toast.className = "fixed top-6 right-6 flex items-center gap-3 bg-beb-danger text-beb-danger-fg px-6 py-4 rounded-2xl shadow-2xl z-50 animate-slideIn"
        toast.innerHTML = `
      <svg class="w-6 h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
      </svg>
      <span class="font-semibold">${message}</span>
    `
        document.body.appendChild(toast)

        // Add animation styles if not present
        if (!document.querySelector("#toast-animations")) {
            const style = document.createElement("style")
            style.id = "toast-animations"
            style.textContent = `
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(100px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .animate-slideIn { animation: slideIn 0.3s ease-out forwards; }
      `
            document.head.appendChild(style)
        }

        setTimeout(() => {
            toast.style.opacity = "0"
            toast.style.transform = "translateX(100px)"
            toast.style.transition = "all 0.3s ease-out"
            setTimeout(() => toast.remove(), 300)
        }, 3000)
    }

    scrollToTop() {
        const formContainer = this.element.closest(".bg-white")
        if (formContainer) {
            formContainer.scrollIntoView({ behavior: "smooth", block: "start" })
        }
    }

    updateReviewPanel() {
        // Check if this is a funnel step form or product form
        if (this.isFunnelStep) {
            this.updateFunnelStepReview()
        } else {
            this.updateProductReview()
        }
    }

    updateProductReview() {
        // Helper to find value by name handling namespace variations with ID fallback
        const getValue = (field) => {
            // 1. Selector by name scoped to form
            const nameSelector = `input[name="product[${field}]"], input[name="super_admin_product[${field}]"], textarea[name="product[${field}]"], textarea[name="super_admin_product[${field}]"]`
            let el = this.element.querySelector(nameSelector)

            // 2. Fallback by ID
            if (!el) {
                el = document.getElementById(`product_${field}`) || document.getElementById(`super_admin_product_${field}`)
            }

            // 3. Fallback to global document query
            if (!el) {
                el = document.querySelector(nameSelector)
            }

            console.log(`[ReviewDebug] Field: ${field}, Found:`, el, "Value:", el?.value)
            return el ? el.value : ""
        }

        console.log("[ReviewDebug] updateProductReview called")

        // Update review summary with current form values
        const title = getValue("title") || "—"
        const description = getValue("description") || "—"

        // Nature (radio) requires special handling (global fallback included)
        const natureSelector = 'input[name="product[product_nature]"]:checked, input[name="super_admin_product[product_nature]"]:checked'
        let natureEl = this.element.querySelector(natureSelector) || document.querySelector(natureSelector)

        // Fallback: search by ID if not found (e.g. if custom radios don't allow :checked query effectively)
        if (!natureEl) {
            // Try to find any checked radio in the group manually
            const radios = document.querySelectorAll('input[name="product[product_nature]"], input[name="super_admin_product[product_nature]"]')
            natureEl = Array.from(radios).find(r => r.checked)
        }

        const nature = natureEl ? natureEl.value : "—"

        const price = getValue("price_regular") || "0"

        const formatSelector = 'input[name="product[format]"]:checked'
        const formatEl = this.element.querySelector(formatSelector)
        const format = formatEl ? formatEl.value : "—"

        // Update review elements
        const reviewTitle = document.getElementById("review-title")
        const reviewDescription = document.getElementById("review-description")
        const reviewNature = document.getElementById("review-nature")
        const reviewPrice = document.getElementById("review-price")
        const reviewFormat = document.getElementById("review-format")

        if (reviewTitle) reviewTitle.textContent = title || "Produit sans titre"
        if (reviewDescription) reviewDescription.textContent = description ? (description.substring(0, 100) + (description.length > 100 ? "..." : "")) : "No description"
        if (reviewNature) reviewNature.textContent = nature ? nature.charAt(0).toUpperCase() + nature.slice(1) : "—"
        if (reviewPrice) reviewPrice.textContent = (price || "0") + " TND"

        let displayFormat = format ? format.charAt(0).toUpperCase() + format.slice(1) : "—"
        if (format === "pdf") displayFormat = "Document"
        if (reviewFormat) reviewFormat.textContent = displayFormat

        // ✅ HANDLE CONTENT SECTIONS (PDF / Video / None)
        const documentPreview = document.getElementById("review-document-preview");
        const videoSection = document.getElementById("review-video-section");
        const noContent = document.getElementById("review-no-content");

        if (!documentPreview || !videoSection || !noContent) return;

        // Reset all sections
        documentPreview.classList.add("hidden");
        videoSection.classList.add("hidden");
        noContent.classList.add("hidden");

        if (format === "pdf") {
            documentPreview.classList.remove("hidden");
            this.updateDocumentPreview();
        } else if (format === "video") {
            videoSection.classList.remove("hidden");
        } else {
            noContent.classList.remove("hidden");
        }
    }

    updateDocumentPreview() {
        const listContainer = document.getElementById("review-documents-list");
        const docEmpty = document.getElementById("review-doc-empty");
        const newTemplate = document.getElementById("review-document-new-template");
        const savedTemplate = document.getElementById("review-document-saved-template");

        if (!listContainer || !newTemplate || !savedTemplate || !docEmpty) {
            console.error("[ReviewDebug] Missing required review elements");
            return;
        }

        // WIPE container to avoid duplication and removed files showing up
        listContainer.innerHTML = '';

        // Find the document upload controller element and its file list
        const uploadElement = document.querySelector('[data-controller="document-upload"]');
        const fileListElement = uploadElement?.querySelector('[data-document-upload-target~="fileList"]');

        let hasFiles = false;

        if (fileListElement) {
            // Iterate over the file list items created by document_upload_controller
            const fileItems = fileListElement.children;

            Array.from(fileItems).forEach(item => {
                const nameEl = item.querySelector("p.font-bold");
                const sizeEl = item.querySelector("p.text-xs");
                const isPersisted = item.hasAttribute("data-persisted");

                if (nameEl) {
                    hasFiles = true;
                    // Choose template based on persistence
                    const template = isPersisted ? savedTemplate : newTemplate;
                    const clone = template.content.cloneNode(true);

                    clone.querySelector("[data-filename]").textContent = nameEl.textContent;
                    clone.querySelector("[data-filesize]").textContent = sizeEl ? sizeEl.textContent : "";

                    listContainer.appendChild(clone);
                }
            });
        } else {
            console.warn("[ReviewDebug] fileListElement NOT found in uploadElement");
        }

        if (hasFiles) {
            docEmpty.classList.add("hidden");
        } else {
            docEmpty.classList.remove("hidden");
        }
    }

    // Handle page type changes for funnel steps (conditional step visibility)
    updateTotalSteps() {
        if (this.isFunnelStep) {
            const pageType = this.element.querySelector('input[name="funnel_step[page_type]"]:checked')?.value
            // Landing pages skip the products step
            this.totalValue = (pageType === "landing" || pageType === "confirmation") ? 3 : 4
        }
    }
}

